import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import AppHeader from '../components/layout/AppHeader';
import Footer from '../components/Footer';

const drawerWidthExpanded = 240;
const drawerWidthCollapsed = 65;

const AppLayout: React.FC = () => {
  const location = useLocation();
  const shouldShowSidebar = location.pathname.startsWith('/app/');
  const isPlatformAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  
  const [drawerCollapsed, setDrawerCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? saved === 'true' : true; // Default to collapsed
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('sidebarCollapsed');
      setDrawerCollapsed(saved === 'true');
    };
    
    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom event for same-window updates
    window.addEventListener('sidebarToggle', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebarToggle', handleStorageChange);
    };
  }, []);

  const currentDrawerWidth = drawerCollapsed ? drawerWidthCollapsed : drawerWidthExpanded;
  const hasDrawer = shouldShowSidebar && isPlatformAuthenticated;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppHeader />
      <Box sx={{ 
        display: 'flex', 
        flex: 1,
        pt: 8, // Space for fixed header
        position: 'relative',
      }}>
        {/* Spacer for sidebar on desktop - full viewport height */}
        {hasDrawer && (
          <Box sx={{ 
            width: currentDrawerWidth, 
            flexShrink: 0,
            display: { xs: 'none', md: 'block' },
            transition: 'width 0.3s ease-in-out',
            bgcolor: '#f8f9fa', // Match sidebar background
            minHeight: 'calc(100vh - 64px)', // Full viewport height minus header
          }} />
        )}
        
        {/* Main content area with footer */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // Allow content to shrink
          minHeight: 'calc(100vh - 64px)', // Full viewport height minus header
        }}>
          <Box sx={{ flex: 1, p: 3 }}>
            <Outlet />
          </Box>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;